import { Button, FormInput, FormWrapper, IconAlert } from "@odmonorepo/core-components";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Card, Spinner, Stack } from "react-bootstrap";
import { BsCheckCircle } from "react-icons/bs";
import * as yup from 'yup';
import { useMutation } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { unifiedApiClient } from "merchant-api/unified";
import type { paths } from "merchant-api/unified/oas";
import ErrorAlert from "../OTPassword/ErrorAlert";
import { trackProveAuth, type ProveAuthAction, type ProveAuthMethod, type ProveFlowType } from "../../Prove/proveAnalytics";
import {
  getProveFallbackError,
  PROVE_REJECTED_STATUS,
  useProveAuthenticator,
  type InstantLinkStatus,
  type ProveFallbackMeta,
} from "../../Prove/useProveAuthenticator";

// Re-exported for the stories and any caller that imported them from here before the SDK
// orchestration moved to useProveAuthenticator (XF-10207).
export { INSTANT_LINK_RESEND_THROTTLE_ERROR_ID, RESEND_THROTTLE_MS } from "../../Prove/useProveAuthenticator";

interface Props {
  associate?: boolean;
  authToken?: string;
  flowType?: ProveFlowType;
  OTPKey: string;
  onContinue?: () => void;
  onFallback?: (data: OTPNotifyResponse) => void;
  onVerify?: (data: ProveCompleteResponse) => void;
  phoneNumber?: string;
  phoneMasked?: string;
}

type ProveMobileAuthFallbackCopy = 'autofill' | 'phoneVerification';

interface ProveMobileAuthContentProps {
  associate?: boolean;
  displayError?: ReactNode;
  error?: number;
  fallbackCopy?: ProveMobileAuthFallbackCopy;
  fallingBack?: boolean;
  flowType: ProveFlowType;
  instantLinkStatus: InstantLinkStatus;
  showEmailFallback: boolean;
  onContinue: () => void;
  onContinueFallback: () => void;
  onFallbackEmail: () => void;
  onResendInstantLink: () => void;
  onResendOtp: () => void;
  onSubmitOtp: (otp: string) => void;
  otpError?: string;
  phoneMasked?: string;
  phoneNumber?: string;
  sent?: boolean;
  showFallbackAction: boolean;
  verifying?: boolean;
}

interface ProveRequestError {
  status?: number;
  data?: unknown;
}

class ProveApiError extends Error implements ProveRequestError {
  status?: number;
  data?: unknown;

  constructor(status?: number, data?: unknown) {
    super('Prove request failed.');
    this.status = status;
    this.data = data;
  }
}

interface ProveCompleteRequest {
  key: string;
}

interface OTPNotifyRequest {
  error?: string;
  key: string;
}

interface OTPNotifyResponse {
  emailMasked: string;
  key: string;
  error?: string;
}

type ProveCompleteResponse = paths["/client/prove/complete"]["post"]["responses"][200]["content"]["application/json"];

const schema = yup.object({
  otp: yup
    .string()
    .required('Enter your security code.')
    .matches(/^[0-9]{4,6}$/, 'Invalid security code. Check that your security code was entered correctly.'),
});

// Prove is decoupled from auth (XF-10207): complete runs on the applicant's existing app-token
// session (minted by create) and issues no cookies. The legacy /login/mobile screen keeps working
// on that same session until the entry path is retired behind the M3 rule.
const completeProve = async (data: ProveCompleteRequest) => {
  const result = await unifiedApiClient.POST('/client/prove/complete' as never, { body: data } as never);

  if (!result.response.ok) throw new ProveApiError(result.response.status, result.error);

  return result.data as unknown as ProveCompleteResponse;
};

const notifyOTP = async (data: OTPNotifyRequest) => {
  const result = await unifiedApiClient.POST('/public/otp/notify', { body: data } as never);

  if (!result.response.ok) throw new ProveApiError(result.response.status, result.error);

  return result.data as unknown as OTPNotifyResponse;
};

const noop = () => undefined;

export const ProveMobileAuth = ({ associate = false, authToken, flowType = 'desktop', OTPKey, phoneNumber, phoneMasked, onContinue = noop, onFallback = noop, onVerify = noop }: Props) => {
  const fallbackStartedRef = useRef(false);
  const [error, setError] = useState<number>();
  const [fallbackError, setFallbackError] = useState<string>();
  const [fallbackCopy, setFallbackCopy] = useState<ProveMobileAuthFallbackCopy>('phoneVerification');
  const [showFallbackAction, setShowFallbackAction] = useState(false);
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  const trackAuth = useCallback((action: ProveAuthAction, properties: Record<string, unknown> = {}, method?: ProveAuthMethod) => {
    trackProveAuth({ action, associate, flowType, method, properties });
  }, [associate, flowType]);
  const { mutate: sendEmailOtp, isPending: fallingBack } = useMutation<OTPNotifyResponse, ProveRequestError, OTPNotifyRequest>({
    mutationFn: notifyOTP,
    throwOnError: false,
    onError: error => {
      authenticator.clearErrors();
      setError(error.status);
      trackAuth('Failed', {}, 'emailOTP');
    },
    onSuccess: data => {
      trackAuth('Sent', {}, 'emailOTP');
      onFallback(data);
    },
  });
  const showProveFallback = (error?: string, meta?: ProveFallbackMeta) => {
    if (fallbackStartedRef.current) return;

    fallbackStartedRef.current = true;
    if (meta?.source === 'ProveAuthenticateError' && flowType !== 'mobile') {
      trackAuth('Failed', { source: 'ProveAuthenticateError' });
    }
    if (import.meta.env.DEV && error && !(associate && meta?.httpStatus === PROVE_REJECTED_STATUS)) console.warn('Prove authentication failed.', error);
    setError(undefined);
    authenticator.reset();
    setFallbackError(error);
    setFallbackCopy(meta?.source === 'ProveCompleteError' ? 'autofill' : 'phoneVerification');
    setShowEmailFallback(false);
    setShowFallbackAction(true);
    const fallbackProperties = meta?.fallbackReason
      ? { fallbackReason: meta.fallbackReason }
      : {
        source: meta?.source ?? 'Unknown',
      };
    const fallbackAction = associate
      ? 'Required'
      : meta?.fallbackReason === 'NoProveMatch' ? 'NoProveMatch' : 'FallbackShown';
    trackAuth(
      fallbackAction,
      associate ? {} : fallbackProperties,
      associate ? 'emailOTP' : undefined
    );
  };
  const { mutateAsync: submitComplete, isPending: verifying } = useMutation<ProveCompleteResponse, ProveRequestError, ProveCompleteRequest>({
    mutationFn: completeProve,
    throwOnError: false,
    onError: error => {
      const fallbackError = getProveFallbackError(error);
      if (!(associate && error.status === PROVE_REJECTED_STATUS)) {
        trackAuth('Submit', {
          result: 'Failure',
        });
      }
      showProveFallback(fallbackError, {
        fallbackReason: error.status === PROVE_REJECTED_STATUS ? 'NoProveMatch' : undefined,
        source: 'ProveCompleteError',
        httpStatus: error.status,
      });
    },
    onSuccess: data => {
      trackAuth('Submit', {
        result: 'Success',
      });
      onVerify(data);
    },
  });
  const authenticator = useProveAuthenticator({
    authToken,
    flowType,
    phoneNumber,
    onFinish: async () => {
      if (flowType !== 'mobile') trackAuth('Completed');
      await submitComplete({ key: OTPKey });
    },
    onFallback: showProveFallback,
    track: trackAuth,
  });

  const displayError = authenticator.resendThrottleErrorId ? <FormattedMessage id={authenticator.resendThrottleErrorId} /> : authenticator.sdkError;

  return (
    <ProveMobileAuthContent
      associate={associate}
      displayError={displayError}
      otpError={authenticator.otpError}
      error={error}
      fallbackCopy={fallbackCopy}
      fallingBack={fallingBack}
      flowType={flowType}
      instantLinkStatus={authenticator.instantLinkStatus}
      showEmailFallback={showEmailFallback}
      onContinue={() => {
        trackAuth('ContinueAfterFallback');
        onContinue();
      }}
      onContinueFallback={() => {
        trackAuth('ContinueAfterFallback');

        if (associate) {
          setShowEmailFallback(true);
          return;
        }

        onContinue();
      }}
      onFallbackEmail={() => {
        trackAuth('Requested', {}, 'emailOTP');
        sendEmailOtp({ key: OTPKey, ...(fallbackError ? { error: fallbackError } : {}) });
      }}
      onResendInstantLink={authenticator.resendInstantLink}
      onResendOtp={authenticator.resendOtp}
      onSubmitOtp={authenticator.submitOtp}
      phoneMasked={phoneMasked}
      phoneNumber={phoneNumber}
      sent={authenticator.sent}
      showFallbackAction={showFallbackAction}
      verifying={verifying}
    />
  );
};

export const ProveMobileAuthContent = ({
  associate = false,
  displayError,
  error,
  fallbackCopy = 'phoneVerification',
  fallingBack = false,
  flowType,
  instantLinkStatus,
  showEmailFallback,
  onContinue,
  onContinueFallback,
  onFallbackEmail,
  onResendInstantLink,
  onResendOtp,
  onSubmitOtp,
  otpError,
  phoneMasked,
  phoneNumber,
  sent = false,
  showFallbackAction,
  verifying = false,
}: ProveMobileAuthContentProps) => {
  const headerContent = displayError
    ? <IconAlert variant="danger">{displayError}</IconAlert>
    : <ErrorAlert status={error} />;
  // Reuse proveBasicInfo copy to match the entry fallback UX.
  const fallbackMessagePrefix = fallbackCopy === 'autofill' ? 'proveBasicInfo.fallback.continue' : 'proveMobileAuth.fallback.continue';
  const activeFallbackMessagePrefix = showEmailFallback ? `${fallbackMessagePrefix}.email` : fallbackMessagePrefix;
  const pageTitleId = (() => {
    if (!showFallbackAction) return 'proveMobileAuth.title';
    if (showEmailFallback) return `${activeFallbackMessagePrefix}.pageTitle`;
    if (fallbackCopy === 'autofill') return 'basicInfo.title';
    return 'proveMobileAuth.title';
  })();
  const instantLinkRecipient = getInstantLinkRecipient(phoneMasked, phoneNumber);
  const instantLinkAction = instantLinkStatus === 'sending' ? (
    <Stack direction="horizontal" gap={2} className="align-items-center fw-bold text-primary">
      <Spinner animation="border" size="sm" role="status" style={{ width: '16px', height: '16px' }} />
      <span>Sending...</span>
    </Stack>
  ) : instantLinkStatus === 'sent' ? (
    <Stack direction="horizontal" gap={2} className="align-items-center text-success">
      <BsCheckCircle size="20px" />
      <span>Sent</span>
    </Stack>
  ) : (
    <Button
      variant="link"
      onClick={onResendInstantLink}
      className="p-0 border-0 fw-bold text-decoration-none"
    >
      Send new link
    </Button>
  );
  const proveFallbackContent = (
    <Stack gap={5}>
      {error ? <ErrorAlert status={error} /> : null}
      <div>
        <div className="h4 mb-2"><FormattedMessage id={`${activeFallbackMessagePrefix}.title`} /></div>
        <div><FormattedMessage id={`${activeFallbackMessagePrefix}.description`} /></div>
      </div>
      {associate && showEmailFallback ? (
        <Button className="w-100" loading={fallingBack} loadingText="Verify Email Address" onClick={onFallbackEmail}>
          <FormattedMessage id={`${activeFallbackMessagePrefix}.button`} />
        </Button>
      ) : (
        <Button className="w-100" onClick={onContinueFallback}>
          <FormattedMessage id={`${fallbackMessagePrefix}.button`} />
        </Button>
      )}
    </Stack>
  );

  return (
    <Stack gap={2} className="align-items-center justify-content-center mt-3">
      <div className="h3 text-center mb-4"><FormattedMessage id={pageTitleId} /></div>
      {showFallbackAction ? <div className="w-100">
        <Card style={{ maxWidth: '600px' }} className='p-5 w-100 mx-auto'>
          {proveFallbackContent}
        </Card>
      </div> : null}
      {!showFallbackAction && flowType === 'desktop' ? <div className="w-100">
        <Card style={{ maxWidth: '600px', boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)' }} className='p-4 p-md-5 w-100 mx-auto border-0'>
          {headerContent}
          <Stack gap={4}>
            <div>
              <div className="h4">Verify Phone Number</div>
              <div>Click the link we sent to {instantLinkRecipient}, then return to this page.</div>
            </div>
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '24px' }}>
              {instantLinkAction}
            </div>
          </Stack>
        </Card>
      </div> : null}
      {!showFallbackAction && flowType === 'mobile' ? <div className="w-100">
        <Card style={{ maxWidth: '600px' }} className='p-5 w-100 mx-auto'>
          {headerContent}
          <FormWrapper schema={schema} reValidateMode="onSubmit" onSubmit={data => onSubmitOtp(data.otp)}>
            <Stack gap={5}>
              <div>
                <div className="h4">Verify your phone number.</div>
                <div>Enter the code we just sent{phoneMasked ? ` to ${phoneMasked}` : ''}.</div>
              </div>
              <OtpErrorSetter otpError={otpError} />
              <FormInput
                name="otp"
                useYupErrors
                formatValue={formatOtp}
                controlProps={{ inputMode: 'numeric' }}
              />
              <div>
                <Button
                  className="w-100"
                  variant="primary"
                  loading={verifying}
                  loadingText={fallingBack ? 'Sending email code...' : 'Submitting...'}
                  type='submit'
                >
                  Submit Code
                </Button>
                <div className="d-flex justify-content-center align-items-center mt-3" style={{ height: '40px' }}>
                  {!sent
                    ? (
                      <Button
                        variant="link"
                        onClick={onResendOtp}
                        className="p-0 border-0 m-1 fs-"
                      >
                        Send new code
                      </Button>
                    ) : (
                      <Stack direction="horizontal" gap={2} className="align-items-center text-success">
                        <BsCheckCircle size="24px"/>
                        <div className="me-3">Sent</div>
                      </Stack>
                    )
                  }
                </div>
              </div>
            </Stack>
          </FormWrapper>
        </Card>
      </div> : null}
    </Stack>
  );
};

function OtpErrorSetter({ otpError }: { otpError?: string }) {
  const { setError, clearErrors } = useFormContext();
  useEffect(() => {
    if (otpError) {
      setError('otp', { type: 'manual', message: otpError });
    } else {
      clearErrors('otp');
    }
  }, [otpError, setError, clearErrors]);
  return null;
}

function formatOtp(value: unknown) {
  return String(value ?? '').replace(/\D/g, '').slice(0, 6);
}

function getInstantLinkRecipient(phoneMasked?: string, phoneNumber?: string) {
  return phoneMasked || formatPhoneNumber(phoneNumber) || 'your phone';
}

function formatPhoneNumber(value?: string) {
  const digits = value?.replace(/\D/g, '');
  if (!digits) return undefined;

  const localDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (localDigits.length !== 10) return value;

  return `(${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
}

export default ProveMobileAuth;
